import { StatisticType } from '../../types/dashboard.type';
import { EsgMonthlyMetricType } from '../../types/esg-monthly-metric.type';
import type { EsgOverviewWidgetId } from '../../types/esg-overview-widget.type';

/**
 * Editorial sections of the Monthly / Overview dashboards. Avoided emissions
 * live in their own section and never share a group with the GHG inventory
 * (GHG Protocol / IFRS S2: avoided emissions are reported outside Scope 1–3).
 */
export type EsgSectionId =
  | 'performance'
  | 'avoided'
  | 'inventory'
  | 'resources'
  | 'healthSafety'
  | 'social'
  | 'operations'
  | 'materialFlows';

export const ESG_MONTHLY_METRIC_SECTION: Record<EsgMonthlyMetricType, EsgSectionId> = {
  [EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED]: 'performance',
  [EsgMonthlyMetricType.WASTE_DIVERSION_RATE]: 'performance',
  [EsgMonthlyMetricType.TOTAL_CO2]: 'avoided',
  [EsgMonthlyMetricType.CARBON_AVOIDED_BY_STREAM]: 'avoided',
  [EsgMonthlyMetricType.CARBON_AVOIDED_BY_MATERIAL]: 'avoided',
  [EsgMonthlyMetricType.RECYCLED_WASTE_BREAKDOWN]: 'materialFlows',
  [EsgMonthlyMetricType.WATER_USE]: 'resources',
  [EsgMonthlyMetricType.ELECTRICITY_USE]: 'resources',
  [EsgMonthlyMetricType.OIL_USE]: 'resources',
  [EsgMonthlyMetricType.GAS_USE]: 'resources',
  [EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT]: 'social',
  [EsgMonthlyMetricType.TRAINING_HOURS_TOTAL]: 'social',
  [EsgMonthlyMetricType.HEALTH_SAFETY]: 'healthSafety',
};

export const ESG_OVERVIEW_WIDGET_SECTION: Record<EsgOverviewWidgetId, EsgSectionId> = {
  summary_waste_diverted: 'performance',
  summary_diversion_rate: 'performance',
  summary_total_co2: 'avoided',
  chart_carbon_stream: 'avoided',
  chart_carbon_material: 'avoided',
  scope_1: 'inventory',
  scope_2: 'inventory',
  utility_water: 'resources',
  utility_electricity: 'resources',
  utility_oil: 'resources',
  utility_gas: 'resources',
  summary_fill_reports: 'social',
  summary_training_hours: 'social',
  summary_health_safety: 'healthSafety',
  chart_recycled: 'materialFlows',
  chart_incoming: 'materialFlows',
};

/** Operational stats: charts belong to material flows, numbers to operations. */
export function operationalStatSection(isChart: boolean): EsgSectionId {
  if (isChart) return 'materialFlows';
  return 'operations';
}

export type MonthlyCardItem =
  | { kind: 'esg'; metric: EsgMonthlyMetricType }
  | { kind: 'ops'; stat: StatisticType };

/** Split the ordered Monthly number cards into editorial sections (order preserved). */
export function groupMonthlyCards(items: readonly MonthlyCardItem[]): Record<EsgSectionId, MonthlyCardItem[]> {
  const groups: Record<EsgSectionId, MonthlyCardItem[]> = {
    performance: [],
    avoided: [],
    inventory: [],
    resources: [],
    healthSafety: [],
    social: [],
    operations: [],
    materialFlows: [],
  };
  for (const item of items) {
    const section =
      item.kind === 'esg' ? ESG_MONTHLY_METRIC_SECTION[item.metric] : operationalStatSection(false);
    groups[section].push(item);
  }
  return groups;
}

/** Overview widgets selected for a section, in pick order. */
export function overviewWidgetsIn(
  section: EsgSectionId,
  selected: readonly EsgOverviewWidgetId[],
): EsgOverviewWidgetId[] {
  return selected.filter((id) => ESG_OVERVIEW_WIDGET_SECTION[id] === section);
}
